import React from "react";
import ReactDOM from "react-dom";
import { useMap } from "../../contexts/MapContext";

/**
 * PersistentMapContainer renders the map DOM that persists across route changes.
 * Uses a portal to render into #content-region for proper CSS positioning.
 * The map instance is created by MapContext and never unmounts during navigation.
 * 
 * The map is only visible when currentPage is set to 'search'.
 */
function PersistentMapContainer() {
  const { mapContainerRef, currentPage, mapInstance } = useMap();
  const [portalContainer] = React.useState(() => {
    const div = document.createElement('div');
    div.className = 'map-region';
    div.style.opacity = '0';
    div.style.pointerEvents = 'none';
    return div;
  });

  // Update visibility based on current page
  React.useEffect(() => {
    const isVisible = currentPage === 'search';
    portalContainer.style.opacity = isVisible ? '1' : '0';
    portalContainer.style.pointerEvents = isVisible ? 'auto' : 'none';
    portalContainer.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  }, [currentPage, portalContainer]);

  function movePortalContainer() {
    const resultsMapHost = currentPage === 'search'
      ? document.querySelector('.search-results-map-host')
      : null;

    if (resultsMapHost && !resultsMapHost.contains(portalContainer)) {
      resultsMapHost.appendChild(portalContainer);
      return true;
    }

    const searchLayout = document.querySelector('.search-layout');
    if (searchLayout && !searchLayout.contains(portalContainer)) {
      searchLayout.insertBefore(portalContainer, searchLayout.firstChild);
      return true;
    }

    return false;
  }

  // Append to the first .search-layout on mount, move when it changes
  React.useEffect(() => {
    const shouldAttach = currentPage === 'search';
    if (!shouldAttach) return;

    if (movePortalContainer()) {
      if (mapInstance && typeof google !== 'undefined') {
        window.requestAnimationFrame(() => google.maps.event.trigger(mapInstance, 'resize'));
      }
      return;
    }

    const observer = new MutationObserver(() => {
      if (movePortalContainer()) {
        observer.disconnect();
        if (mapInstance && typeof google !== 'undefined') {
          window.requestAnimationFrame(() => google.maps.event.trigger(mapInstance, 'resize'));
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [currentPage, portalContainer, mapInstance]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (portalContainer.parentNode) {
        portalContainer.parentNode.removeChild(portalContainer);
      }
    };
  }, [portalContainer]);

  return ReactDOM.createPortal(
    <div ref={mapContainerRef} className="map-container" />,
    portalContainer
  );
}

export default PersistentMapContainer;
